"use client";

import {
  sdkActionManager,
  useEmbedNonStylesConfig,
  useEmbedStyles,
  useIsEmbed,
} from "@calcom/embed-core/embed-iframe";
import { useRouterQuery } from "@calcom/lib/hooks/useRouterQuery";
import useTheme from "@calcom/lib/hooks/useTheme";
import { UserAvatar } from "@calcom/ui/components/avatar";
import { Icon } from "@calcom/ui/components/icon";
import { OrgBanner } from "@calcom/ui/components/organization-banner";
import { UnpublishedEntity } from "@calcom/ui/components/unpublished-entity";
import { EventTypeDescriptionLazy as EventTypeDescription } from "@calcom/web/modules/event-types/components";
import EmptyPage from "@calcom/web/modules/event-types/components/EmptyPage";
import type { getServerSideProps } from "@server/lib/[user]/getServerSideProps";
import classNames from "classnames";
import type { InferGetServerSidePropsType } from "next";
import Link from "next/link";
import { Toaster } from "sonner";

export type PageProps = InferGetServerSidePropsType<typeof getServerSideProps>;
export function UserPage(props: PageProps) {
  const { users, profile, eventTypes, entity } = props;

  const [user] = users; //To be used when we only have a single user, not dynamic group
  useTheme(profile.theme);

  const isBioEmpty = !user.bio || !user.bio.replace("<p><br></p>", "").length;

  const isEmbed = useIsEmbed(props.isEmbed);
  const eventTypeListItemEmbedStyles = useEmbedStyles("eventTypeListItem");
  const shouldAlignCentrallyInEmbed = useEmbedNonStylesConfig("align") !== "left";
  const shouldAlignCentrally = !isEmbed || shouldAlignCentrallyInEmbed;
  const {
    // So it doesn't display in the Link (and make tests fail)
    user: _user,
    orgSlug: _orgSlug,
    redirect: _redirect,
    ...query
  } = useRouterQuery();

  if (entity.considerUnpublished) {
    return (
      <div className="flex h-full min-h-[calc(100dvh)] items-center justify-center">
        <UnpublishedEntity {...entity} />
      </div>
    );
  }

  const isEventListEmpty = eventTypes.length === 0;
  const isOrg = !!user?.profile?.organization;

  return (
    <>
      <div className={classNames(shouldAlignCentrally ? "mx-auto" : "", isEmbed ? "max-w-3xl" : "")}>
        <main
          className={classNames(
            shouldAlignCentrally ? "mx-auto" : "",
            isEmbed ? "border-booker border-booker-width  bg-default rounded-md" : "",
            "max-w-3xl px-4 py-12"
          )}>
          <div className="border-subtle bg-default text-default mb-8 overflow-hidden rounded-xl border">
            {isOrg && user.profile.organization?.bannerUrl ? (
              <OrgBanner
                alt={user.profile.organization.name ?? "Organization banner"}
                imageSrc={user.profile.organization.bannerUrl}
                className="p-1 border border-subtle rounded-xl w-full object-cover"
              />
            ) : (
              <div className="border-subtle relative h-24 overflow-hidden border-b bg-[#0f0e0c] sm:h-32">
                {/* Zen Design Labs racing stripes (Figma: Zenfolio 20-7) */}
                <svg
                  className="absolute inset-0 block h-full w-full"
                  viewBox="0 0 750 224"
                  preserveAspectRatio="none"
                  fill="none"
                  aria-hidden="true">
                  <rect y="-66" width="750" height="290" fill="#0F0E0C" />
                  <path
                    d="M-200 136.44C-200 136.44 393.62 136.44 559.105 136.44C724.59 136.44 724.59 235 724.59 235"
                    stroke="#7B61FF"
                    strokeWidth="20.48"
                  />
                  <path
                    d="M-198.504 107C-198.504 107 438.69 107 587.624 107C736.558 107 759 235 759 235"
                    stroke="#E85D3A"
                    strokeWidth="17.92"
                  />
                  <path
                    d="M-198.504 121.08C-198.504 121.08 412.738 121.08 571.656 121.08C730.574 121.08 739.551 233.72 739.551 233.72"
                    stroke="#FFB800"
                    strokeWidth="15.36"
                  />
                </svg>
              </div>
            )}
            <div className="p-4 sm:px-6 sm:pb-6">
              <div
                className={classNames(
                  "flex items-end gap-4",
                  !(isOrg && user.profile.organization?.bannerUrl) && "-mt-14 sm:-mt-16"
                )}>
                <UserAvatar
                  size="xl"
                  user={{
                    avatarUrl: user.avatarUrl,
                    profile: user.profile,
                    name: profile.name,
                    username: profile.username,
                  }}
                  className={classNames(
                    isOrg && user.profile.organization?.bannerUrl
                      ? "-mt-14"
                      : "bg-default ring-default rounded-full ring-4"
                  )}
                />
                <h1 className="font-cal text-emphasis mb-1 text-xl" data-testid="name-title">
                  {profile.name}
                  {!isOrg && user.verified && (
                    <Icon
                      name="badge-check"
                      className="mx-1 -mt-1 inline h-6 w-6 fill-blue-500 text-white dark:text-black"
                    />
                  )}
                  {isOrg && (
                    <Icon
                      name="badge-check"
                      className="mx-1 -mt-1 inline h-6 w-6 fill-yellow-500 text-white dark:text-black"
                    />
                  )}
                </h1>
              </div>
              {!isBioEmpty && (
                <>
                  {/* biome-ignore lint/security/noDangerouslySetInnerHtml: Content is sanitized via safeBio */}
                  <div
                    className="text-default wrap-break-word mt-4 text-sm [&_a]:text-blue-500 [&_a]:underline [&_a]:hover:text-blue-600"
                    dangerouslySetInnerHTML={{ __html: props.safeBio }}
                  />
                </>
              )}
            </div>
          </div>

          <div
            className={classNames("rounded-md ", !isEventListEmpty && "border-subtle border")}
            data-testid="event-types">
            {eventTypes.map((type) => (
              <Link
                key={type.id}
                style={{ display: "flex", ...eventTypeListItemEmbedStyles }}
                prefetch={false}
                href={{
                  pathname: `/${user.profile.username}/${type.slug}`,
                  query,
                }}
                passHref
                onClick={async () => {
                  sdkActionManager?.fire("eventTypeSelected", {
                    eventType: type,
                  });
                }}
                className="bg-default border-subtle dark:bg-cal-muted dark:hover:bg-subtle hover:bg-cal-muted group relative border-b transition first:rounded-t-md last:rounded-b-md last:border-b-0"
                data-testid="event-type-link">
                <Icon
                  name="arrow-right"
                  className="text-emphasis absolute right-4 top-4 h-4 w-4 opacity-0 transition-opacity group-hover:opacity-100"
                />
                {/* Don't prefetch till the time we drop the amount of javascript in [user][type] page which is impacting score for [user] page */}
                <div className="block w-full p-5">
                  <div className="flex flex-wrap items-center">
                    <h2 className="text-default pr-2 text-sm font-semibold">{type.title}</h2>
                  </div>
                  <EventTypeDescription eventType={type} isPublic={true} shortenDescription />
                </div>
              </Link>
            ))}
          </div>

          {isEventListEmpty && <EmptyPage name={profile.name || "User"} />}
        </main>
        <Toaster position="bottom-right" />
      </div>
    </>
  );
}

export default UserPage;
